/**
 * OperatorSidebar — 200px expanded / 56px collapsed black sidebar for Direction A.
 *
 * W66.D: Sidebar IA cleanup — consolidated from 7 sections into 4 per W66A spec.
 * Approved by Bryan K. Short, Founder and CEO, Mi6 Group — 2026-05-31.
 *
 * Sections (left rail — destinations visited once or twice per session):
 *   OPERATE   — Workspace, My Plan (rep), Today, Jumpstart, Command View, Accounts, Cadence, Upload Leads
 *   PLAN      — Methodology, Sales Plan (mgr+), Question Bank, Funnel (mgr+)
 *   REFERENCE — Manuals, Flowcharts, Galaxy (hero tile), Opportunity DNA, Tower,
 *               Pipeline DNA, Team DNA (mgr+), Pipeline Tower (mgr+), Templates
 *   ADMIN     — Reporting (mgr+), Activity Audit (mgr+), Forecast (mgr+),
 *               Leaderboard (mgr+), Coaching (mgr+), Marketing (mgr+), Settings
 *
 * Retired from sidebar (routes kept as deep links where noted):
 *   Dashboard (/dashboards/manager) — reachable via /dashboards deep link
 *   /question-tree — retired from sidebar; route kept per spec
 *   Old CORE/VISUALS/REPORT section split eliminated
 *
 * Collapse behavior:
 *   - Toggle button at top of sidebar
 *   - Auto-collapse when window width < 1280px
 *   - Persists preference in localStorage under "aip:sidebar-collapsed"
 *   - When collapsed: icons only, labels as title tooltips on hover
 */
import { useLocation } from "wouter";
import { useState, useEffect } from "react";
import {
  LayoutGrid,
  Network,
  FileStack,
  GraduationCap,
  Activity,
  Settings,
  LogOut,
  Target,
  Megaphone,
  LineChart,
  Home,
  PhoneCall,
  Upload,
  Users,
  Building2,
  Layers,
  TrendingUp,
  Trophy,
  BarChart2,
  PanelLeftClose,
  PanelLeftOpen,
  BookOpen,
  GitBranch,
  Map,
  Telescope,
  CalendarRange,
  Workflow,
  Rocket,
  Library,
  Flag,
} from "lucide-react";
import { tokens } from "./tokens";
import { getSession, logout } from "@/lib/session";
import GalaxyEntryButton from "@/components/galaxy/GalaxyEntryButton";
import { ImpersonationPicker } from "../ImpersonationPicker";
import { getImpersonationTarget, clearImpersonationTarget, clearScopedSession, subscribe as subscribeImpersonation, type ImpersonationTarget } from "@/lib/impersonation";
import { queryClient } from "@/App";
import { api } from "@/lib/session";
import { AIP_VERSION } from "@/version";

/**
 * W66.X15: Subscribe to impersonation state so the sidebar re-renders
 * immediately when Bryan picks "View as User". Without this the sidebar
 * keeps reading session.user even after the impersonation target is set.
 */
function useImpersonationTarget(): ImpersonationTarget | null {
  const [target, setTarget] = useState<ImpersonationTarget | null>(getImpersonationTarget);
  useEffect(() => {
    return subscribeImpersonation(() => setTarget(getImpersonationTarget()));
  }, []);
  return target;
}

const STORAGE_KEY = "aip:sidebar-collapsed";

type SideItem = {
  label: string;
  href: string;
  Icon: any;
  match?: (path: string) => boolean;
};

// ─── OPERATE section ──────────────────────────────────────────────────────────

/** W66.B: Workspace — first item in OPERATE, all roles. */
const WORKSPACE_ITEM: SideItem = {
  label: "Workspace",
  href: "/workspace",
  Icon: LayoutGrid,
  match: (loc: string) => loc === "/workspace" || loc.startsWith("/workspace/"),
};

/** My Plan — rep-only daily landing. Pinned for BDR / AE. */
const REP_HOME: SideItem[] = [
  { label: "My Plan", href: "/my-plan", Icon: Home },
];

/** Today — quick daily view for all roles. */
const TODAY_ITEM: SideItem = {
  label: "Today",
  href: "/today",
  Icon: CalendarRange,
};

/** Jumpstart — W66.J 30/60/90 plan tool, all roles. Placed after Today in OPERATE. */
const JUMPSTART_ITEM: SideItem = {
  label: "Jumpstart",
  href: "/jumpstart",
  Icon: Rocket,
  match: (loc: string) => loc === "/jumpstart" || loc.startsWith("/jumpstart/"),
};

/** Command View — all roles per Decision 1 (LOCKED). */
const COMMAND_VIEW_ITEM: SideItem = {
  label: "Command View",
  href: "/command-view",
  Icon: Telescope,
  match: (loc: string) =>
    loc === "/command-view" ||
    loc.startsWith("/command-view/") ||
    loc.includes("/command-view"),
};

/** Accounts — all roles. */
const ACCOUNTS_ITEM: SideItem = {
  label: "Accounts",
  href: "/accounts",
  Icon: Building2,
};

/** BDR-specific cadence + upload, shown only for bdr role. */
const BDR_OPERATE: SideItem[] = [
  { label: "Cadence",      href: "/cadence/bdr",    Icon: PhoneCall },
  { label: "Upload Leads", href: "/upload-contacts", Icon: Upload },
];

/** AE-specific cadence + upload, shown only for ae role. */
const AE_OPERATE: SideItem[] = [
  { label: "Cadence",      href: "/cadence/ae",      Icon: PhoneCall },
  { label: "Upload Leads", href: "/upload-contacts",  Icon: Upload },
];

// ─── PLAN section ─────────────────────────────────────────────────────────────

/** PLAN — all roles base items (promoted from TopNav-only per W66.D spec). */
const PLAN_ALL: SideItem[] = [
  { label: "Methodology",   href: "/methodology",   Icon: Workflow,
    match: (loc) => loc.startsWith("/methodology") },
  { label: "Question Bank", href: "/question-bank", Icon: GraduationCap,
    match: (loc) => loc.startsWith("/question-bank") },
];

/** PLAN — manager/exec additions. */
const PLAN_MANAGER: SideItem[] = [
  { label: "Sales Plan", href: "/sales-plan", Icon: Map,
    match: (loc) => loc.startsWith("/sales-plan") },
  // W66.H: Funnel — manager/exec only per spec gating
  { label: "Funnel",     href: "/funnel",     Icon: GitBranch,
    match: (loc) => loc.startsWith("/funnel") },
];

// ─── REFERENCE section ────────────────────────────────────────────────────────

/** REFERENCE items rendered before Galaxy hero tile. */
const REFERENCE_PRE_GALAXY: SideItem[] = [
  // Promoted from TopNav-only
  { label: "Manuals",           href: "/manuals",   Icon: BookOpen,
    match: (loc) => loc.startsWith("/manuals") },
  // W66.X17: Reference Library — doctrine, manifestos, CP models
  { label: "Reference Library", href: "/library",   Icon: Library,
    match: (loc) => loc.startsWith("/library") },
  // Keep until W66.C absorbs it into Methodology
  { label: "Flowcharts", href: "/flowcharts", Icon: GitBranch,
    match: (loc) => loc.startsWith("/flowcharts") },
];

/** REFERENCE items rendered after Galaxy hero tile. */
const REFERENCE_POST_GALAXY: SideItem[] = [
  { label: "Opportunity DNA", href: "/opportunity-dna", Icon: Network,
    match: (loc) => loc.startsWith("/opportunity-dna") || loc.startsWith("/dna") },
  { label: "Tower",           href: "/tower",           Icon: Layers },
  { label: "Pipeline DNA",    href: "/pipeline-dna",    Icon: BarChart2,
    match: (loc) => loc.startsWith("/pipeline-dna") },
  { label: "Templates",       href: "/templates",       Icon: FileStack,
    match: (loc) => loc.startsWith("/templates") },
];

/** REFERENCE — manager/exec additions.
 *  Decision 3 (LOCKED): Pipeline Tower stays standalone now; W66.C folds it later. */
const REFERENCE_MANAGER: SideItem[] = [
  { label: "Team DNA",       href: "/team-dna",       Icon: Users,
    match: (loc) => loc.startsWith("/team-dna") },
  { label: "Pipeline Tower", href: "/pipeline-tower", Icon: Layers,
    match: (loc) => loc.startsWith("/pipeline-tower") },
];

// ─── ADMIN section ────────────────────────────────────────────────────────────

/** ADMIN — manager/exec only. */
const ADMIN_MANAGER: SideItem[] = [
  { label: "Reporting",      href: "/reporting",        Icon: LineChart,
    match: (loc) => loc.startsWith("/reporting") },
  { label: "Activity Audit", href: "/activity/audit",   Icon: Activity,
    match: (loc) => loc.startsWith("/activity/audit") || loc.startsWith("/audit") },
  { label: "Forecast",       href: "/forecast",         Icon: TrendingUp,
    match: (loc) => loc.startsWith("/forecast") },
  { label: "Leaderboard",    href: "/leaderboard",      Icon: Trophy,
    match: (loc) => loc.startsWith("/leaderboard") },
  { label: "Coaching",       href: "/coaching",         Icon: Target,
    match: (loc) => loc.startsWith("/coaching") },
  { label: "Marketing",      href: "/marketing",        Icon: Megaphone,
    match: (loc) => loc.startsWith("/marketing") },
];

/** Settings — visible to all roles (rep self-service + manager/exec full). */
const SETTINGS_ITEM: SideItem = {
  label: "Settings",
  href: "/settings/account",
  Icon: Settings,
  match: (loc) => loc.startsWith("/settings"),
};

/** Launch Readiness — admin-only external link, opens in a new tab. */
const LAUNCH_READINESS_ITEM = {
  label: "Launch Readiness",
  href: "https://mi6-launch.pplx.app",
  Icon: Flag,
};

const ROLES = [
  { key: "manager",   label: "Manager",   href: "/dashboards/manager" },
  { key: "ae",        label: "AE",        href: "/dashboards/ae" },
  { key: "bdr",       label: "BDR",       href: "/dashboards/bdr" },
  { key: "executive", label: "Executive", href: "/dashboards/executive" },
  { key: "marketing", label: "Marketing", href: "/dashboards/marketing" },
  { key: "se",        label: "SE",        href: "/dashboards/se" },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function SectionHeader({ children, collapsed }: { children: React.ReactNode; collapsed: boolean }) {
  if (collapsed) {
    // Show a slim horizontal divider instead of label text when collapsed
    return (
      <div
        aria-hidden="true"
        style={{
          margin: "10px 12px 6px",
          height: 1,
          background: "rgba(255,255,255,0.08)",
        }}
      />
    );
  }
  return (
    <div
      style={{
        padding: "16px 16px 6px",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.18em",
        color: tokens.color.red,
        textTransform: "uppercase",
        fontFamily: tokens.font.body,
      }}
    >
      {children}
    </div>
  );
}

function SideRow({
  item,
  active,
  onNav,
  collapsed,
}: {
  item: SideItem;
  active: boolean;
  onNav: (href: string) => void;
  collapsed: boolean;
}) {
  const { Icon } = item;
  return (
    <button
      type="button"
      title={collapsed ? item.label : undefined}
      onClick={() => onNav(item.href)}
      className="group w-full flex items-center transition-colors relative"
      style={{
        gap: collapsed ? 0 : 10,
        padding: collapsed ? "8px 0" : "7px 12px 7px 16px",
        justifyContent: collapsed ? "center" : "flex-start",
        background: active ? "#26262B" : "transparent",
        color: active ? "#FFFFFF" : "#C9C9CC",
        fontSize: 13,
        fontFamily: tokens.font.body,
        boxShadow: active ? "inset 2px 0 8px rgba(220, 38, 38, 0.15)" : undefined,
        textAlign: "left",
        border: "none",
        cursor: "pointer",
        width: "100%",
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = "rgba(31,31,35,0.55)";
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
      }}
    >
      {active && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0, top: 0, bottom: 0,
            width: 3,
            background: tokens.color.red,
          }}
        />
      )}
      <Icon size={15} strokeWidth={1.75} style={{ opacity: active ? 1 : 0.7, flexShrink: 0 }} />
      {!collapsed && <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>}
    </button>
  );
}

/** External-link row — same look as SideRow but renders an anchor that opens
 *  in a new tab. Used for admin-only Launch Readiness. */
function ExternalSideRow({
  item,
  collapsed,
}: {
  item: { label: string; href: string; Icon: any };
  collapsed: boolean;
}) {
  const { Icon } = item;
  return (
    <a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      title={collapsed ? item.label : undefined}
      className="group w-full flex items-center transition-colors relative"
      style={{
        gap: collapsed ? 0 : 10,
        padding: collapsed ? "8px 0" : "7px 12px 7px 16px",
        justifyContent: collapsed ? "center" : "flex-start",
        background: "transparent",
        color: "#C9C9CC",
        fontSize: 13,
        fontFamily: tokens.font.body,
        textAlign: "left",
        textDecoration: "none",
        cursor: "pointer",
        width: "100%",
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "rgba(31,31,35,0.55)"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "transparent"; }}
    >
      <Icon size={15} strokeWidth={1.75} style={{ opacity: 0.7, flexShrink: 0 }} />
      {!collapsed && <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>}
    </a>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export default function OperatorSidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const [location, setLocation] = useLocation();
  const session = getSession();
  const [signingOut, setSigningOut] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const impersonationTarget = useImpersonationTarget();
  const impersonating = !!impersonationTarget;

  // Collapse state — read from localStorage on mount
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) return stored === "true";
    } catch { /* ignore */ }
    // Default: auto-collapse on small desktops
    return typeof window !== "undefined" && window.innerWidth < 1280;
  });

  // Auto-collapse when window shrinks below 1280px; restore when it grows back
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1280px)");
    function handleChange(e: MediaQueryListEvent) {
      // Only auto-change if user hasn't explicitly set a preference in this session
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === null) {
        setCollapsed(!e.matches);
      }
    }
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  function toggleCollapse() {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try { await logout(); } catch { /* ignore */ }
    setLocation("/login");
    onNavigate?.();
  }

  // W66.X15: effective identity — impersonation target takes precedence over
  // real session so the sidebar card, nav gating, and role-specific items
  // all reflect the user Bryan is viewing as.
  const userName = impersonationTarget?.name || session?.name || "";
  const userRole = impersonationTarget?.role || session?.role || "";
  const initials = userName
    ? userName.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()
    : "...";
  const tenantLabel = "Mi6 Group";
  // "View as" is a dev/QA impersonation affordance — gate it to admin operators
  // only. Ordinary managers and executives should not see it. The app has no
  // literal "admin" role, so the canonical admin identities are the dual-role
  // accounts plus the explicit admin emails (Attilius and Bryan, the founder).
  const ADMIN_EMAILS = ["attilius@mi6group.com", "bryan.short@mi6group.com", "bryan.short+t1@mi6group.com"];
  const isAdmin =
    !!session?.isDualRole ||
    (!!session?.email && ADMIN_EMAILS.includes(session.email.toLowerCase()));
  const canViewAs = isAdmin;

  const isManager = userRole === "manager" || userRole === "executive";
  const isRep     = userRole === "bdr" || userRole === "ae";

  function navigate(href: string) {
    setLocation(href);
    onNavigate?.();
  }

  function isActive(item: SideItem) {
    if (item.match) return item.match(location);
    return location === item.href || location.startsWith(item.href + "/");
  }

  const sidebarWidth = collapsed
    ? tokens.space.sidebarCollapsedWidth
    : tokens.space.sidebarWidth;

  return (
    <aside
      style={{
        position: "fixed",
        top: 0, left: 0, bottom: 0,
        width: sidebarWidth,
        background: tokens.color.black,
        color: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        fontFamily: tokens.font.body,
        zIndex: 50,
        borderRight: `1px solid ${tokens.color.navySoft}`,
        transition: "width 0.2s ease",
        overflow: "hidden",
      }}
    >
      {/* Top wordmark + collapse toggle */}
      <div
        style={{
          padding: collapsed ? "16px 0" : "20px 16px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          gap: 8,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img
              src="/brand/attilius-elephant.png"
              alt="Attilius"
              style={{ width: 36, height: "auto", display: "block", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }}
            />
            <div style={{ lineHeight: 1.1 }}>
              <div style={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: 14, letterSpacing: -0.2 }}>
                Attilius
              </div>
              <div style={{ fontSize: 9, letterSpacing: "0.16em", color: "#6B6B70", fontWeight: 600, marginTop: 2 }}>
                {AIP_VERSION}
              </div>
            </div>
          </div>
        )}
        {collapsed && (
          <img
            src="/brand/attilius-elephant.png"
            alt="Attilius"
            title="Attilius V3.1"
            style={{ width: 28, height: "auto", display: "block", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }}
          />
        )}
        <button
          type="button"
          onClick={toggleCollapse}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "#6B6B70",
            padding: 4,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#FFFFFF"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#6B6B70"; }}
        >
          {collapsed
            ? <PanelLeftOpen size={14} strokeWidth={1.75} />
            : <PanelLeftClose size={14} strokeWidth={1.75} />
          }
        </button>
      </div>

      {/* Navigation sections */}
      <nav style={{ flex: 1, overflowY: "auto", overflowX: "hidden", paddingBottom: 12 }}>

        {/* ── OPERATE ───────────────────────────────────────────────── */}
        {/* W66.D: Daily/weekly execution — every role has at least one item. */}
        <SectionHeader collapsed={collapsed}>Operate</SectionHeader>

        {/* W66.B: Workspace — first item, all roles */}
        <SideRow
          item={WORKSPACE_ITEM}
          active={isActive(WORKSPACE_ITEM)}
          onNav={navigate}
          collapsed={collapsed}
        />

        {/* My Plan — rep only */}
        {isRep && REP_HOME.map((item) => (
          <SideRow key={item.href} item={item} active={isActive(item)} onNav={navigate} collapsed={collapsed} />
        ))}

        {/* Today — all roles */}
        <SideRow
          item={TODAY_ITEM}
          active={isActive(TODAY_ITEM)}
          onNav={navigate}
          collapsed={collapsed}
        />

        {/* W66.J: Jumpstart — 30/60/90 plan tool, all roles. After Today. */}
        <SideRow
          item={JUMPSTART_ITEM}
          active={isActive(JUMPSTART_ITEM)}
          onNav={navigate}
          collapsed={collapsed}
        />

        {/* Command View — ALL roles per Decision 1 (LOCKED) */}
        <SideRow
          item={COMMAND_VIEW_ITEM}
          active={isActive(COMMAND_VIEW_ITEM)}
          onNav={navigate}
          collapsed={collapsed}
        />

        {/* Accounts — all roles */}
        <SideRow
          item={ACCOUNTS_ITEM}
          active={isActive(ACCOUNTS_ITEM)}
          onNav={navigate}
          collapsed={collapsed}
        />

        {/* BDR cadence + upload */}
        {userRole === "bdr" && BDR_OPERATE.map((item) => (
          <SideRow key={item.href} item={item} active={isActive(item)} onNav={navigate} collapsed={collapsed} />
        ))}

        {/* AE cadence + upload */}
        {userRole === "ae" && AE_OPERATE.map((item) => (
          <SideRow key={item.href} item={item} active={isActive(item)} onNav={navigate} collapsed={collapsed} />
        ))}

        {/* ── PLAN ──────────────────────────────────────────────────── */}
        {/* W66.D: Strategic/planning surfaces. Promoted from TopNav-only. */}
        <SectionHeader collapsed={collapsed}>Plan</SectionHeader>

        {/* Methodology + Question Bank — all roles */}
        {PLAN_ALL.map((item) => (
          <SideRow key={item.href} item={item} active={isActive(item)} onNav={navigate} collapsed={collapsed} />
        ))}

        {/* Sales Plan + Funnel — manager/exec only (W66.H gating per spec) */}
        {isManager && PLAN_MANAGER.map((item) => (
          <SideRow key={item.href} item={item} active={isActive(item)} onNav={navigate} collapsed={collapsed} />
        ))}

        {/* ── REFERENCE ─────────────────────────────────────────────── */}
        {/* W66.D: Read-only / semi-static reference material. */}
        <SectionHeader collapsed={collapsed}>Reference</SectionHeader>

        {/* Manuals + Flowcharts — promoted from TopNav-only */}
        {REFERENCE_PRE_GALAXY.map((item) => (
          <SideRow key={item.href} item={item} active={isActive(item)} onNav={navigate} collapsed={collapsed} />
        ))}

        {/* W49: Galaxy hero tile — context-aware routing; kept as hero tile */}
        <GalaxyEntryButton variant="siderail" collapsed={collapsed} />

        {/* Opportunity DNA, Tower, Pipeline DNA, Templates — all roles */}
        {REFERENCE_POST_GALAXY.map((item) => (
          <SideRow key={item.href} item={item} active={isActive(item)} onNav={navigate} collapsed={collapsed} />
        ))}

        {/* Team DNA + Pipeline Tower — manager/exec
            Decision 3 (LOCKED): Pipeline Tower stays standalone; W66.C folds it later */}
        {isManager && REFERENCE_MANAGER.map((item) => (
          <SideRow key={item.href} item={item} active={isActive(item)} onNav={navigate} collapsed={collapsed} />
        ))}

        {/* ── ADMIN ─────────────────────────────────────────────────── */}
        {/* W66.D: Config/management — manager/exec primary, Settings for all. */}
        <SectionHeader collapsed={collapsed}>Admin</SectionHeader>

        {/* Reporting, Activity Audit, Forecast, Leaderboard, Coaching, Marketing — mgr/exec */}
        {isManager && ADMIN_MANAGER.map((item) => (
          <SideRow key={item.href} item={item} active={isActive(item)} onNav={navigate} collapsed={collapsed} />
        ))}

        {/* Launch Readiness — admin only; external link, opens in a new tab */}
        {isAdmin && (
          <ExternalSideRow item={LAUNCH_READINESS_ITEM} collapsed={collapsed} />
        )}

        {/* Settings — all roles (rep self-service + manager/exec full config) */}
        <SideRow
          item={SETTINGS_ITEM}
          active={isActive(SETTINGS_ITEM)}
          onNav={navigate}
          collapsed={collapsed}
        />

      </nav>

      {/* Role-switcher footer — hidden when collapsed to keep icon rail clean */}
      {!collapsed && (
        <div
          style={{
            margin: 12,
            padding: "10px 12px",
            background: tokens.color.navySoft,
            borderRadius: 8,
            fontFamily: tokens.font.body,
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
            <div
              style={{
                width: 30, height: 30, borderRadius: "50%",
                background: tokens.color.red,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, color: "#FFFFFF",
                fontFamily: tokens.font.display,
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <div style={{ lineHeight: 1.2, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#FFFFFF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {userName}
              </div>
              <div style={{ fontSize: 10, color: "#8E8E94", textTransform: "uppercase" }}>
                {userRole} · {tenantLabel}
              </div>
            </div>
          </div>

          {/*
            D-074 (2026-05-22): "View as" opens a real impersonation picker instead
            of a route-only role dropdown. Server-side impersonation session sends
            X-Impersonate-User-Id on every request so manager sees rep data exactly.
          */}
          {canViewAs && !impersonating && (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: tokens.color.black,
                border: `1px solid ${tokens.color.navySoft}`,
                borderRadius: 6,
                padding: "5px 8px",
                fontSize: 11,
                color: "#FFFFFF",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: tokens.font.body,
              }}
            >
              <span style={{ opacity: 0.7, fontWeight: 400 }}>View as: </span>
              User
            </button>
          )}

          {/* D-078 (2026-05-22): Exit impersonation — always visible while impersonating. */}
          {canViewAs && impersonating && (
            <button
              type="button"
              onClick={async () => {
                try { await api("/api/admin/impersonate/stop", { method: "POST" }); } catch { /* ignore */ }
                clearImpersonationTarget();
                // Wave I (B-BDR6): wipe React Query cache + role-scoped localStorage.
                queryClient.clear();
                clearScopedSession();
                window.location.reload();
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: "#7F1D1D",
                border: "1px solid #450A0A",
                borderRadius: 6,
                padding: "5px 8px",
                fontSize: 11,
                color: "#FFFFFF",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: tokens.font.body,
              }}
              data-testid="sidebar-exit-impersonation"
            >
              <span style={{ opacity: 0.85, fontWeight: 500 }}>Impersonating — </span>
              Exit
            </button>
          )}

          <ImpersonationPicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
          {/* Old VIEW_AS_OPTIONS / ROLES list kept for reference but not rendered. */}
          {false && ROLES.length}

          {/* Sign out */}
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            aria-label="Sign out"
            data-testid="sidebar-signout"
            style={{
              marginTop: 10,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              background: tokens.color.black,
              color: "#FFFFFF",
              border: `1px solid ${tokens.color.navyHover}`,
              borderRadius: 6,
              padding: "7px 10px",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.04em",
              cursor: signingOut ? "not-allowed" : "pointer",
              opacity: signingOut ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (!signingOut) (e.currentTarget as HTMLButtonElement).style.background = tokens.color.navyHover;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = tokens.color.black;
            }}
          >
            <LogOut size={13} strokeWidth={1.75} />
            <span>{signingOut ? "Signing out\u2026" : "Sign out"}</span>
          </button>

          {/* Support contact */}
          <div
            style={{
              marginTop: 10,
              fontSize: 10,
              color: "#A0A0A6",
              textAlign: "center",
              letterSpacing: "0.02em",
            }}
          >
            Need help?{" "}
            <a
              href="mailto:support@mi6group.com"
              style={{ color: "#D0D0D6", textDecoration: "underline" }}
            >
              support@mi6group.com
            </a>
            <div style={{ marginTop: 6, display: "flex", justifyContent: "center", gap: 8 }}>
              <a href="#/legal/terms" style={{ color: "#A0A0A6", textDecoration: "underline" }}>Terms</a>
              <span style={{ color: "#6F6F75" }}>·</span>
              <a href="#/legal/privacy" style={{ color: "#A0A0A6", textDecoration: "underline" }}>Privacy</a>
            </div>
          </div>
        </div>
      )}

      {/* Collapsed footer — sign out icon only */}
      {collapsed && (
        <div
          style={{
            padding: "12px 0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            title="Sign out"
            aria-label="Sign out"
            data-testid="sidebar-signout"
            style={{
              background: "transparent",
              border: "none",
              cursor: signingOut ? "not-allowed" : "pointer",
              color: "#6B6B70",
              padding: 8,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: signingOut ? 0.6 : 1,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#FFFFFF"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#6B6B70"; }}
          >
            <LogOut size={15} strokeWidth={1.75} />
          </button>
        </div>
      )}
    </aside>
  );
}
